import Gleap from 'gleap';
import type { PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';

const TRACKING_ACTIVE = process.env.NODE_ENV === 'production';

export interface GleapContextData {
  gleap?: GleapService;
}

export interface GleapContextProps extends PropsWithChildren {
  token?: string;
  config?: Partial<GleapConfig>;
}

export interface GleapConfig {
  environment?: 'dev' | 'staging' | 'prod';
}

const context = createContext<GleapContextData>({} as GleapContextData);

const { Provider } = context;

export const useGleapContext = () => useContext(context);

export const GleapProvider = ({
  children,
  token,
  config,
}: GleapContextProps) => {
  const instance = GleapService.getInstance(token, config);
  return <Provider value={{ gleap: instance }}>{children}</Provider>;
};

export class GleapService {
  private static instance: GleapService;

  private constructor(token?: string, config?: Partial<GleapConfig>) {
    if (token) {
      Gleap.initialize(token);

      if (config?.environment) {
        Gleap.setEnvironment(config.environment);
      }
    }
  }

  static getInstance(
    token?: string,
    config?: Partial<GleapConfig>
  ): GleapService {
    if (!GleapService.instance && TRACKING_ACTIVE) {
      GleapService.instance = new GleapService(token, config);
    }
    return GleapService.instance;
  }

  identifyUser(userId?: string, userProperties?: Record<string, any>) {
    if (userId) {
      const gleapUserProperties = {
        name: userProperties?.name || null,
        email: userProperties?.email || null,
        phone: userProperties?.phone || null,
        value: userProperties?.value || null,
        plan: userProperties?.plan || null,
        companyId: userProperties?.companyId || null,
        companyName: userProperties?.companyName || null,
        customData: userProperties?.customData || {},
      };

      Gleap.identify(userId, gleapUserProperties);
    }
  }

  resetUser() {
    Gleap.clearIdentity();
  }

  getUserId() {
    return Gleap.getIdentity();
  }

  trackEvent(eventName: string, eventData?: Record<string, any>) {
    if (this.isUserIdentified()) {
      Gleap.trackEvent(eventName, eventData);
    }
  }

  updateContact(properties: Record<string, any>) {
    Gleap.updateContact(properties);
  }

  isUserIdentified(): boolean {
    return Gleap.isUserIdentified();
  }
}
